import { AppstoreAddOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { Form, Layout } from 'antd';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { SCard, SDetailLayout } from 'app/components/StaycationUI';
import { CustomReactMarkdown } from 'app/components/commons/CustomMarkdown/CustomMarkdown';
import { EmptyState } from 'app/components/pages/Extranet/commons/EmptyState/EmptyState';
import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { useHotelCopywriting } from 'app/hooks/data/core/admin/hotels/useHotelCopywriting';
import { hotelPath, useGetHotel } from 'app/hooks/data/hotels/useHotel';
import type { HotelDisclaimer } from 'app/typings';
import { DisclaimerKind } from 'app/typings/disclaimers';
import { format, toDayjs } from 'app/utils/dayjs';
import type { Dayjs } from 'app/utils/dayjs';

import { DisclaimerContent } from '../../../../commons/DisclaimerContent/DisclaimerContent';

import './Copywriting.scss';
import { CopywritingContent } from './CopywritingContent';

const { Content } = Layout;

type Props = {
  hotelId: number;
  headerMenu: ReactNode;
  headerExtra?: ReactNode[];
};

export type CopywritingFormData = {
  description?: string;
  valueProposition?: string;
  disclaimers?: HotelDisclaimer[];
};

export const Copywriting = ({ headerMenu, headerExtra, hotelId }: Props) => {
  const { mode, enterViewMode, enterEditMode, isEditMode } =
    useContext(ViewEditModeContext);
  const [form] = Form.useForm<CopywritingFormData>();
  const queryClient = useQueryClient();
  const [isDirty, setIsDirty] = useState(false);

  const { data: hotel, isFetching: isFetchHotelLoading } = useGetHotel(hotelId);
  const { mutateAsync: saveCopywriting } = useHotelCopywriting(hotelId);

  const initialValues = useMemo(
    () => ({
      description: hotel?.description ?? '',
      valueProposition: hotel?.valueProposition ?? '',
      disclaimers:
        hotel?.disclaimers
          ?.map((disclaimer) => ({
            ...disclaimer,

            dateRange: [
              toDayjs(disclaimer.startDate),
              toDayjs(disclaimer.endDate),
            ],
          }))
          .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0)) ?? [],
    }),
    [hotel]
  );

  const onFinish = useCallback(
    async (values: CopywritingFormData) => {
      if (!hotel) {
        return;
      }

      const mappedDisclaimers =
        values.disclaimers?.map(
          (
            disclaimer: HotelDisclaimer & {
              dateRange?: [Dayjs | null, Dayjs | null];
            },
            index: number
          ) => {
            const { dateRange, ...rest } = disclaimer;

            return {
              ...rest,
              startDate: dateRange?.[0]
                ? format(dateRange[0], 'YYYY-MM-DD')
                : format(new Date(), 'YYYY-MM-DD'),
              endDate: dateRange?.[1]
                ? format(dateRange[1], 'YYYY-MM-DD')
                : null,
              priority: index + 1,
            };
          }
        ) ?? [];

      await saveCopywriting({
        description: values.description ?? '',
        valueProposition: values.valueProposition ?? '',
        disclaimers: mappedDisclaimers,
      });

      queryClient.invalidateQueries([hotelPath, { hotelId }]);
      enterViewMode();
      setIsDirty(false);
    },
    [hotel, hotelId, queryClient, saveCopywriting, enterViewMode]
  );

  const onCancel = useCallback(() => {
    form.resetFields();
    setIsDirty(false);
    enterViewMode();
  }, [form, enterViewMode]);

  const description = Form.useWatch('description', form);

  if (!hotel || isFetchHotelLoading) {
    return null;
  }

  return (
    <SDetailLayout
      title={hotel.name}
      backRoute="/hotels"
      mode={mode}
      onEdit={enterEditMode}
      onSave={() => form.submit()}
      onCancel={onCancel}
      reset={() => form.resetFields()}
      isDirty={isDirty}
      headerMenu={headerMenu}
      headerExtra={headerExtra}
      isValid={form.getFieldsError().every((item) => item.errors.length > 0)}
    >
      <Content className="hotel-detail__content copywriting">
        <Form
          form={form}
          onFinish={onFinish}
          initialValues={initialValues}
          className="copywriting-form"
          onValuesChange={() => setIsDirty(true)}
        >
          <div className="copywriting-left-column">
            <SCard
              title="Identity"
              className="scard-with-pictures copywriting-card"
            >
              <CopywritingContent isEditMode={isEditMode} />
            </SCard>

            <DisclaimerContent
              kind={DisclaimerKind.IMPORTANT_INFORMATION}
              filterByKind={false}
            />
          </div>

          {isEditMode && (
            <SCard
              title="Description preview"
              className="description-preview-card"
            >
              {description ? (
                <div className="view-text__area">
                  <CustomReactMarkdown>{description}</CustomReactMarkdown>
                </div>
              ) : (
                <EmptyState
                  title="No description"
                  description="Enter a description to see the formatted preview here using markdown."
                  icon={<AppstoreAddOutlined className="empty-state" />}
                />
              )}
            </SCard>
          )}
        </Form>
      </Content>
    </SDetailLayout>
  );
};
